import { useState } from "react";
import { Link } from "react-router-dom";
import { SettingHeader } from "components";
import { faqList } from "data";
import { FAQItemType } from "data/FAQList";

const CATEGORIES = [
  "전체",
  "서비스 이용 & 예약",
  "결제 & 환불",
  "서비스 판매 & 등록",
  "수익 정산 & 세금 신고",
  "고객지원 & 문제해결",
];

const FAQItem = ({
  item,
  isOpen,
  onToggle,
}: {
  item: FAQItemType;
  isOpen: boolean;
  onToggle: () => void;
}) => {
  // Format bold text (**text**) and newlines (\n) cleanly
  const renderFormattedAnswer = (text: string) => {
    return text.split("\n").map((line, idx) => {
      const parts = line.split(/(\*\*.*?\*\*)/g);
      return (
        <p key={idx} className={`${line.startsWith("•") ? "pl-3 text-achromatic-700 font-medium my-0.5" : "my-1"}`}>
          {parts.map((part, pIdx) => {
            if (part.startsWith("**") && part.endsWith("**")) {
              return <strong key={pIdx} className="font-bold text-achromatic-900">{part.slice(2, -2)}</strong>;
            }
            return part;
          })}
        </p>
      );
    });
  };

  return (
    <div
      className={`border rounded-2xl transition-all duration-200 overflow-hidden mb-3.5 ${
        isOpen
          ? "border-primary-200 bg-white shadow-sm ring-1 ring-primary-100"
          : "border-achromatic-100 bg-white hover:border-achromatic-200"
      }`}
    >
      <button
        onClick={onToggle}
        className="w-full flex justify-between items-center p-5 text-left transition-colors hover:bg-achromatic-50/50"
      >
        <div className="flex flex-col gap-1.5 pr-4">
          <span className="inline-flex self-start px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-primary-50 text-primary-600 border border-primary-100">
            {item.category}
          </span>
          <span className="font-bold text-base text-achromatic-900 leading-snug">
            {item.question}
          </span>
        </div>
        <div
          className={`w-8 h-8 rounded-full flex items-center justify-center transition-transform duration-200 flex-shrink-0 ${
            isOpen ? "bg-primary-500 text-white rotate-180" : "bg-achromatic-100 text-achromatic-500"
          }`}
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {isOpen && (
        <div className="px-5 pb-5 pt-1 text-sm text-achromatic-600 leading-relaxed border-t border-achromatic-100/80 bg-achromatic-50/30 animate-fadeIn">
          <div className="pt-3">{renderFormattedAnswer(item.answer)}</div>
        </div>
      )}
    </div>
  );
};

const FAQSettingComponent = () => {
  const [selectedCategory, setSelectedCategory] = useState("전체");
  const [searchQuery, setSearchQuery] = useState("");
  const [openId, setOpenId] = useState<number | null>(1); // Open first item by default

  const filteredFaqs = faqList.filter((faq) => {
    const matchesCategory =
      selectedCategory === "전체" || faq.category === selectedCategory;
    const matchesSearch =
      searchQuery.trim() === "" ||
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const toggleAccordion = (id: number) => {
    setOpenId(openId === id ? null : id);
  };

  return (
    <div className="flex flex-col w-full border-achromatic-100 border-r min-h-[80vh]">
      <SettingHeader title="자주 묻는 질문 (FAQ)" />

      <div className="p-6 flex flex-col gap-6 max-w-4xl">
        {/* Search Bar */}
        <div className="relative">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="궁금하신 내용을 검색해 보세요 (예: 결제, 취소, 수수료, 원천징수)..."
            className="w-full pl-11 pr-10 py-3.5 border border-achromatic-200 rounded-2xl text-sm focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-100 transition-all bg-white shadow-sm"
          />
          <span className="absolute left-4 top-3.5 text-achromatic-400 text-lg">🔍</span>
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute right-4 top-3.5 text-achromatic-400 hover:text-achromatic-700 text-sm font-bold"
            >
              ✕
            </button>
          )}
        </div>

        {/* Category Tabs */}
        <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-none">
          {CATEGORIES.map((cat) => (
            <button
              key={cat}
              onClick={() => {
                setSelectedCategory(cat);
                setOpenId(null);
              }}
              className={`px-4 py-2 rounded-xl text-xs font-bold whitespace-nowrap transition-all ${
                selectedCategory === cat
                  ? "bg-primary-500 text-white shadow-md shadow-primary-500/20"
                  : "bg-white text-achromatic-600 border border-achromatic-200 hover:bg-achromatic-50"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* FAQ List */}
        <div className="flex flex-col mt-2">
          {filteredFaqs.length === 0 ? (
            <div className="bg-white rounded-2xl border border-achromatic-100 p-12 text-center flex flex-col items-center gap-3">
              <span className="text-4xl">🔎</span>
              <p className="font-bold text-achromatic-800 text-base">검색 결과가 없습니다.</p>
              <p className="text-xs text-achromatic-400">
                단어의 철자가 정확한지 확인하시거나 다른 키워드로 검색해 보세요.
              </p>
              <button
                onClick={() => {
                  setSearchQuery("");
                  setSelectedCategory("전체");
                }}
                className="mt-2 px-4 py-2 bg-achromatic-100 text-achromatic-700 rounded-xl text-xs font-bold hover:bg-achromatic-200 transition-colors"
              >
                전체 목록 보기
              </button>
            </div>
          ) : (
            filteredFaqs.map((item) => (
              <FAQItem
                key={item.id}
                item={item}
                isOpen={openId === item.id}
                onToggle={() => toggleAccordion(item.id)}
              />
            ))
          )}
        </div>

        {/* Support Banner */}
        <div className="bg-gradient-to-r from-primary-500 to-primary-600 rounded-2xl p-6 text-white flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 shadow-lg shadow-primary-500/10 mt-4">
          <div>
            <h4 className="font-extrabold text-base mb-1">원하시는 답변을 찾지 못하셨나요?</h4>
            <p className="text-xs text-primary-100 leading-relaxed">
              1:1 고객문의 게시판 또는 이메일(help@jellypunch.com)로 남겨주시면 담당자가 친절하게 신속히 답변해 드립니다.
            </p>
          </div>
          <Link
            to="/setting/contact"
            className="px-5 py-2.5 bg-white text-primary-600 text-xs font-extrabold rounded-xl hover:bg-primary-50 transition-all whitespace-nowrap shadow-sm flex-shrink-0"
          >
            1:1 문의하기 →
          </Link>
        </div>
      </div>
    </div>
  );
};

export default FAQSettingComponent;